// React and Hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// Services/API
import { getPriceListAPI } from "services/api/prices/api";
import { initialPriceListDetailForm } from "services/api/prices/consts";
import { PriceListDetailFormValues } from "services/api/prices/types";

// Services/Validation
import { Errors } from "services/forms/validation/types";

// UI Components
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";

// Custom Components
import PriceListDetailCard from "./components/PriceListDetailCard/PriceListDetailCard";
import PriceListDeleteCard from "./components/PriceListDeleteCard/PriceListDeleteCard";

// Constants
import { CALCULATION_VEHICLE_GROUP_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_VEHICLE_GROUP } from "app/components/Sidebar/consts";

// Types
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { PriceListDetailPageCmp, PriceListDetailPageProps } from "./types";

// Routes
import { Routes } from "ui/modules/prices/navigation";

const PriceListDetailPage: PriceListDetailPageCmp = (
  props: PriceListDetailPageProps
) => {
  const { priceListId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<PriceListDetailFormValues>(
    initialPriceListDetailForm
  );

  const navigate = useNavigate();

  const title = CALCULATION_VEHICLE_GROUP_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (priceListId) fetchPriceList(priceListId);
  }, [priceListId]);

  const fetchPriceList = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialPriceListDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getPriceListAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_VEHICLE_GROUP.text,
      route: SIDEBAR_ITEM_CALCULATION_VEHICLE_GROUP.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.PriceListSearchPageRouter, {
        priceListId,
      })
    );
  }, []);

  if (!priceListId) return <></>;

  return (
    <>
      <div className="bg-white border-b border-slate-200 rounded-t-md">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={`${t(title.text)}`}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
              <div className="flex gap-2">
                <Button
                  size="large"
                  variant="remove"
                  children={t(
                    "admin.calculation.setting.vehiclegroup.detail.delete"
                  )}
                  onClick={() => setShowDeleteModal(true)}
                  startIcon="/media/icons/trash.svg"
                />
              </div>
            </div>
          </PageHeader>
        </div>
      </div>
      <div className="px-4 py-6  lg:px-4">
        <PriceListDetailCard priceListId={priceListId} />
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t("admin.calculation.setting.vehiclegroup.delete.title")}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <PriceListDeleteCard
              priceListId={priceListId}
              onSuccess={handleDeleteSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

PriceListDetailPage.route = Routes.PriceListDetailPageRouter;

export default PriceListDetailPage;
